import { useEffect, useRef, useState } from 'react'

import type { RefObject } from 'react'

export interface UseDeferredFadeSwapOptions {
  /** When false the hook is a no-op: incoming values update immediately with no animation. */
  enabled: boolean
  /** Fade duration in ms. Must match the CSS transition duration applied to the container. */
  durationMs: number
  /**
   * When true the first isLoading true→false cycle updates displayedValue immediately (no fade).
   * All subsequent cycles fade normally. Useful when an initial spinner already covers the first load.
   */
  skipFirstTransition?: boolean
}

export interface UseDeferredFadeSwapResult<T> {
  /** The value currently rendered in the DOM. Frozen during fade-out; swaps at fade-in start. */
  displayedValue: T
  /** Drives the visible/hidden CSS class on the container. */
  isVisible: boolean
  /**
   * True once the first loading cycle has completed. Consumers can derive an "is-first-load"
   * boolean by negating this value (e.g. isFirstLoad = !isFirstTransitionDone).
   */
  isFirstTransitionDone: boolean
  /** Attach to the fading container so the hook can measure its height. */
  containerRef: RefObject<HTMLDivElement>
  /** Set during fade-out to prevent layout shift; cleared after fade-in settles. */
  preservedMinHeight: number | undefined
}

export function useDeferredFadeSwap<T>(
  incomingValue: T,
  isLoading: boolean | undefined,
  options: UseDeferredFadeSwapOptions,
): UseDeferredFadeSwapResult<T> {
  const { enabled, durationMs, skipFirstTransition = false } = options

  const [displayedValue, setDisplayedValue] = useState<T>(incomingValue)
  const [isVisible, setIsVisible] = useState(true)
  const [preservedMinHeight, setPreservedMinHeight] = useState<number | undefined>(undefined)
  // When skipFirstTransition is true, mark the first cycle as not done so it skips the animation.
  const [isFirstTransitionDone, setIsFirstTransitionDone] = useState(!skipFirstTransition)

  const containerRef = useRef<HTMLDivElement>(null)
  const prevLoadingRef = useRef<boolean | undefined>(undefined)
  const fadeStartTimeRef = useRef<number>(0)
  // Always holds the latest incoming value so setTimeout closures can read it fresh.
  const latestValueRef = useRef<T>(incomingValue)
  latestValueRef.current = incomingValue
  // Ref to the inner cleanup timer so the effect cleanup can cancel it even when set asynchronously
  // inside the outer timer callback.
  const innerTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    const wasLoading = prevLoadingRef.current
    const isNowLoading = !!isLoading
    prevLoadingRef.current = isNowLoading

    // Skip the initial render — we only act on loading state transitions.
    if (wasLoading === undefined) return

    let outerTimer: ReturnType<typeof setTimeout> | undefined

    // Loading started: begin fade-out if enabled and past the first transition.
    if (!wasLoading && isNowLoading && enabled && isFirstTransitionDone) {
      fadeStartTimeRef.current = Date.now()
      setIsVisible(false)
      if (containerRef.current) {
        setPreservedMinHeight(containerRef.current.offsetHeight)
      }
    }

    // Loading ended.
    if (wasLoading && !isNowLoading) {
      if (!isFirstTransitionDone) {
        // First cycle (skipFirstTransition=true): update immediately and mark first cycle done.
        setDisplayedValue(latestValueRef.current)
        setIsFirstTransitionDone(true)
        setPreservedMinHeight(undefined)
      } else if (enabled) {
        // Subsequent cycles with fade: wait for fade-out to complete, then swap content and fade in.
        const elapsed = Date.now() - fadeStartTimeRef.current
        const remainingFadeOut = Math.max(0, durationMs - elapsed + 30)
        outerTimer = setTimeout(() => {
          // Swap content only after fade-out so new data never flashes through during the transition.
          setDisplayedValue(latestValueRef.current)
          setIsVisible(true)
          innerTimerRef.current = setTimeout(() => {
            setPreservedMinHeight(undefined)
          }, durationMs + 80)
        }, remainingFadeOut)
      } else {
        // Fade disabled (enabled=false): update the displayed value immediately.
        setDisplayedValue(latestValueRef.current)
      }
    }

    return () => {
      clearTimeout(outerTimer)
      // innerTimerRef.current may have been set after outerTimer fired, so read it via ref.
      clearTimeout(innerTimerRef.current)
      innerTimerRef.current = undefined
    }
  }, [isLoading, isFirstTransitionDone, enabled, durationMs])

  return { displayedValue, isVisible, isFirstTransitionDone, containerRef, preservedMinHeight }
}
